import {
  AlertCircle,
  ArrowLeft,
  Bot,
  Check,
  ChevronRight,
  Circle,
  FileText,
  Gauge,
  Languages,
  Loader2,
  MessageSquareText,
  Minimize2,
  Plus,
  RefreshCw,
  Settings2,
  ShieldCheck,
  Sparkles,
  MessageCircle,
  Trash2,
  Wrench,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useToast } from "../../components/ui/toast";
import { useDiscardWarning } from "../../hooks/use-discard-warning";
import { core } from "../../lib/api";
import { cn } from "../../lib/utils";

// `awaiting_input` is new: the builder is an agent loop now, so a turn can
// end with a question for the operator instead of a finished prompt.
type SessionStatus =
  | "pending"
  | "running"
  | "awaiting_input"
  | "awaiting_review"
  | "done"
  | "failed";
type BuilderSection = "persona" | "settings" | "objective" | "conversation" | "knowledge";

interface BuilderVariable {
  key: string;
  value: string;
}

interface TransferTarget {
  label: string;
  number: string;
}

interface BuilderInput {
  schema_version: number;
  agent_name: string;
  company_name: string;
  industry: string;
  region: string;
  call_direction: "inbound" | "outbound";
  call_purpose: string;
  primary_language: string;
  secondary_language: string;
  voice_gender: "Female" | "Male";
  voice_tone: string;
  opening_message: string;
  call_flow: string;
  variables: BuilderVariable[];
  kb_enabled: boolean;
  kb_content: string;
  disclose_ai: boolean;
  recording_consent: boolean;
  disclosure_text: string;
  digression_handling: string;
  retry_fallback: string;
  max_retries: number;
  live_transfer_enabled: boolean;
  transfer_numbers: TransferTarget[];
  transfer_triggers: string[];
  after_hours_behavior: string;
}

interface QualityIssue {
  severity?: "critical" | "major" | "minor";
  category?: string;
  issue?: string;
  recommendation?: string;
  evidence?: string;
}

interface PromptSession {
  session_id: string;
  agent_id: string;
  status: SessionStatus;
  stage: string;
  template_id: string;
  agent_name: string;
  company_name: string;
  use_case: string;
  language: "english" | "hindi" | "multilingual";
  additional_context: string;
  builder_input?: BuilderInput;
  clarification_questions?: string[];
  clarifications?: Record<string, string>;
  // What the builder asked, when status is `awaiting_input`.
  agent_question?: string | null;
  // When the current turn was claimed. The elapsed clock is anchored to this
  // rather than to when this tab happened to mount, so a reload mid-build
  // does not restart it at 0:00.
  started_at?: string | null;
  generation?: {
    system_prompt?: string;
    first_message?: string;
    // Which architecture the agent chose from the brief -- it is not a
    // setting the operator picks.
    architecture?: "stewart" | "clove";
    rationale?: string;
    // The tool decision, with the agent's reason for each. `why` and
    // `used_in` are the point: tools are chosen deliberately now, not
    // pattern-matched from slot names.
    tools?: Array<{ name?: string; why?: string; used_in?: string }>;
    // Capabilities the flow needed that no deployable tool provides.
    proposed_tools?: Array<{ name?: string; purpose?: string }>;
    variables?: Array<{ key?: string; direction?: string; description?: string }>;
    findings?: Array<{ rule?: string; passed?: boolean; evidence?: string; severity?: string }>;
    validation_status?: "success" | "warning" | "failed_review_required";
  } | null;
  quality?: {
    ready?: boolean;
    blocking_count?: number;
    issues?: QualityIssue[];
    validation_status?: string;
    // What the build actually cost, reported by the harness rather than by
    // the model.
    cost?: number;
    tokens?: number;
    findings?: Array<{ rule?: string; passed?: boolean; evidence?: string; severity?: string }>;
  } | null;
  error?: string | null;
  accepted_version?: {
    version: number;
    version_name: string;
    agent_version_id: string;
  } | null;
}

const EMPTY_FORM: BuilderInput = {
  schema_version: 1,
  agent_name: "Untitled Agent",
  company_name: "",
  industry: "",
  region: "",
  call_direction: "inbound",
  call_purpose: "",
  primary_language: "English",
  secondary_language: "None",
  voice_gender: "Female",
  voice_tone: "professional",
  opening_message: "",
  call_flow: "",
  variables: [],
  kb_enabled: false,
  kb_content: "",
  disclose_ai: true,
  recording_consent: false,
  disclosure_text: "",
  digression_handling: "Answer briefly, then resume the script",
  retry_fallback: "Transfer to a human agent",
  max_retries: 2,
  live_transfer_enabled: false,
  transfer_numbers: [{ label: "", number: "" }],
  transfer_triggers: [],
  after_hours_behavior: "",
};

const INDUSTRIES = [
  "E-commerce",
  "Healthcare",
  "Finance",
  "Real Estate",
  "Education",
  "Insurance",
  "SaaS",
  "Travel & Hospitality",
  "Logistics",
  "Other",
];

const REGIONS = [
  ["", "Not specified"],
  ["IN", "India"],
  ["US", "United States"],
  ["CA", "Canada"],
  ["GB", "United Kingdom"],
  ["AE", "United Arab Emirates"],
  ["SA", "Saudi Arabia"],
  ["QA", "Qatar"],
  ["SG", "Singapore"],
  ["AU", "Australia"],
  ["DE", "Germany"],
  ["FR", "France"],
];

const LANGUAGES = ["English", "Hindi", "Spanish", "French", "German"];

const TRANSFER_TRIGGERS = [
  "Caller explicitly asks for a human",
  "Caller sounds angry or distressed",
  "Issue is outside the agent's scope",
  "Call is after business hours",
];

// The builder is an agent loop, not a fixed pipeline, so there are no
// intermediate stages to count -- it decides its own steps. Showing a nine-step
// bar that advanced on a timer suggested progress the backend never reported.
// Elapsed time is the honest signal, and a build runs for several minutes.
const stages = [
  ["preparing", "Reading the brief"],
  ["architecting", "Writing the prompt"],
  ["review", "Ready for review"],
] as const;

const TYPICAL_BUILD_SECONDS = 300;

const sections: Array<{ id: BuilderSection; label: string; icon: typeof Bot }> = [
  { id: "persona", label: "Persona", icon: Bot },
  { id: "settings", label: "Settings", icon: Settings2 },
  { id: "objective", label: "Call purpose", icon: Gauge },
  { id: "conversation", label: "Conversation", icon: MessageSquareText },
  { id: "knowledge", label: "Knowledge", icon: FileText },
];

function formFromSession(session: PromptSession): BuilderInput {
  if (session.builder_input) {
    return {
      ...EMPTY_FORM,
      ...session.builder_input,
      variables: session.builder_input.variables || [],
      transfer_numbers: session.builder_input.transfer_numbers?.length
        ? session.builder_input.transfer_numbers
        : [{ label: "", number: "" }],
      transfer_triggers: session.builder_input.transfer_triggers || [],
    };
  }
  return {
    ...EMPTY_FORM,
    agent_name: session.agent_name || "Untitled Agent",
    company_name: session.company_name || "",
    call_purpose: session.use_case || "",
    primary_language: session.language === "hindi" ? "Hindi" : "English",
    secondary_language: session.language === "multilingual" ? "Hindi" : "None",
    kb_enabled: Boolean(session.additional_context?.trim()),
    kb_content: session.additional_context || "",
  };
}

function fieldClass(error = false) {
  return cn(
    // text-ink is load-bearing: bg-elevated sets an author background, which
    // makes Safari paint <select> popups with author colours. Without an
    // explicit colour the options inherit whatever the UA supplies and can
    // render invisible against the popup surface on OS-dark Safari.
    "w-full rounded-md border bg-elevated px-3 py-2.5 text-sm text-ink outline-none",
    "focus:border-brand focus:ring-2 focus:ring-brand/15",
    error ? "border-danger" : "border-line",
  );
}

function Toggle({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <label className="flex cursor-pointer items-center justify-between gap-4 border-b border-line py-3 last:border-0">
      <span className="text-sm font-semibold text-ink">{label}</span>
      <span className="relative h-6 w-11 shrink-0">
        <input
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(event) => onChange(event.target.checked)}
          className="peer sr-only"
        />
        <span className="absolute inset-0 rounded-full bg-line transition-colors peer-checked:bg-ink peer-disabled:opacity-50" />
        <span className="absolute left-1 top-1 h-4 w-4 rounded-full bg-white transition-transform peer-checked:translate-x-5" />
      </span>
    </label>
  );
}

export function AutoBuilderPage() {
  const { sessionId: routeSessionId = "" } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const isNew = routeSessionId === "new";
  const templateId = searchParams.get("template_id") || "";
  const [activeSessionId, setActiveSessionId] = useState(isNew ? "" : routeSessionId);
  const [session, setSession] = useState<PromptSession | null>(null);
  const [form, setForm] = useState<BuilderInput>(EMPTY_FORM);
  const [clarifications, setClarifications] = useState<Record<string, string>>({});
  const [activeSection, setActiveSection] = useState<BuilderSection>("persona");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [progressDismissed, setProgressDismissed] = useState(false);
  const [answer, setAnswer] = useState("");
  const [buildStartedAt, setBuildStartedAt] = useState<number | null>(null);
  const [elapsed, setElapsed] = useState(0);

  // Read through a ref, not through state: `load` must keep a stable identity
  // or the initialize effect below re-runs on every keystroke.
  const dirtyRef = useRef(false);
  useEffect(() => {
    dirtyRef.current = dirty;
  }, [dirty]);

  const load = useCallback(async (id: string) => {
    if (!id) return null;
    const next = await core<PromptSession>(
      `/api/v1/prompt-builder/sessions/${encodeURIComponent(id)}`,
    );
    setSession(next);
    // A load that lands while the operator has unsaved edits must not
    // overwrite them with the server's older copy.
    if (!dirtyRef.current) {
      setForm(formFromSession(next));
      setClarifications(next.clarifications || {});
      setDirty(false);
    }
    return next;
  }, []);

  useEffect(() => {
    let active = true;
    const initialize = async () => {
      if (!isNew) {
        await load(routeSessionId);
        return;
      }
      const templates = await core<Array<{
        id: string;
        agent_name: string;
        use_case: string;
        direction: "inbound" | "outbound";
      }>>("/api/v1/prompt-builder/templates");
      const template = templates.find((item) => item.id === templateId);
      const initial: PromptSession = {
        session_id: "",
        agent_id: "",
        status: "pending",
        stage: "describe",
        template_id: templateId,
        agent_name: template?.agent_name || "Untitled Agent",
        company_name: "",
        use_case: template?.use_case || "",
        language: "english",
        additional_context: "",
      };
      if (!active) return;
      setSession(initial);
      setForm({
        ...EMPTY_FORM,
        agent_name: initial.agent_name,
        call_purpose: initial.use_case,
        call_direction: template?.direction || "inbound",
      });
    };
    initialize()
      .catch((error) => active && toast.danger("Could not load AutoBuilder", (error as Error).message))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [isNew, load, routeSessionId, templateId, toast]);

  useEffect(() => {
    if (session?.status !== "running") return undefined;
    const timer = window.setInterval(() => {
      load(activeSessionId).catch(() => undefined);
    }, 1800);
    return () => window.clearInterval(timer);
  }, [activeSessionId, load, session?.status]);

  useEffect(() => {
    if (session?.status !== "running") {
      setProgressDismissed(false);
    }
  }, [session?.status]);

  // A build takes minutes. Without a clock the screen looks hung, and the
  // backend reports no sub-steps to count.
  useEffect(() => {
    if (session?.status !== "running") {
      setBuildStartedAt(null);
      setElapsed(0);
      return undefined;
    }
    const serverStart = session?.started_at ? Date.parse(session.started_at) : NaN;
    const startedAt = Number.isNaN(serverStart)
      ? (buildStartedAt ?? Date.now())
      : serverStart;
    if (buildStartedAt !== startedAt) setBuildStartedAt(startedAt);
    const timer = window.setInterval(
      () => setElapsed(Math.floor((Date.now() - startedAt) / 1000)),
      1000,
    );
    return () => window.clearInterval(timer);
  }, [session?.status, session?.started_at, buildStartedAt]);

  const gaps = useMemo(() => {
    const next: string[] = [];
    if (!form.company_name.trim()) next.push("Company name");
    if (!form.call_purpose.trim()) next.push("Call purpose");
    if (!form.call_flow.trim()) next.push("Call flow");
    if (form.kb_enabled && !form.kb_content.trim()) next.push("Knowledge content");
    if (form.recording_consent && !form.disclosure_text.trim()) next.push("Disclosure text");
    if (form.live_transfer_enabled && !form.transfer_numbers.some((item) => item.number.trim())) {
      next.push("Transfer number");
    }
    return next;
  }, [form]);

  const updateForm = <K extends keyof BuilderInput>(key: K, value: BuilderInput[K]) => {
    setForm((current) => ({ ...current, [key]: value }));
    setDirty(true);
    if (session?.stage === "clarifications" || session?.stage === "ready") {
      setSession((current) => current ? { ...current, stage: "describe", clarification_questions: [] } : current);
      setClarifications({});
    }
  };

  const ensureSession = async () => {
    let id = activeSessionId;
    let isFirstSave = false;
    if (!id) {
      const created = await core<PromptSession>("/api/v1/prompt-builder/sessions", {
        method: "POST",
        body: JSON.stringify({
          template_id: templateId || null,
          agent_name: form.agent_name,
        }),
      });
      id = created.session_id;
      isFirstSave = true;
      setActiveSessionId(id);
      setSession(created);
    }
    const prepared = await core<PromptSession>(
      `/api/v1/prompt-builder/sessions/${encodeURIComponent(id)}`,
      {
        method: "PATCH",
        body: JSON.stringify({ builder_input: form, clarifications }),
      },
    );
    setSession(prepared);
    setDirty(false);
    if (isFirstSave) {
      // Only once the brief is saved. Changing the URL flips `isNew` and
      // re-runs the initialize effect, which reloads the session from the
      // server -- navigating first raced that reload against this PATCH, and
      // when the read won, the freshly created session had no `builder_input`
      // yet, so the operator's whole brief was replaced by an empty form and
      // "Generate prompt" went permanently dead.
      navigate(`/home/autobuilder/single/${encodeURIComponent(id)}`, { replace: true });
    }
    return { id, prepared };
  };

  const evaluate = async () => {
    if (gaps.length) {
      toast.warning("Complete the required fields", gaps.join(", "));
      return;
    }
    try {
      setBusy(true);
      const { id } = await ensureSession();
      const reviewed = await core<PromptSession>(
        `/api/v1/prompt-builder/sessions/${encodeURIComponent(id)}/evaluate`,
        { method: "POST", body: JSON.stringify({ force: true }) },
      );
      setSession(reviewed);
      setClarifications(reviewed.clarifications || {});
    } catch (error) {
      toast.danger("Brief review failed", (error as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const start = async () => {
    if (gaps.length) {
      toast.warning("Complete the required fields", gaps.join(", "));
      return;
    }
    try {
      setBusy(true);
      const { id } = await ensureSession();
      const next = await core<PromptSession>(
        `/api/v1/prompt-builder/sessions/${encodeURIComponent(id)}/start`,
        {
          method: "POST",
          body: JSON.stringify({ idempotency_key: `start-${id}` }),
        },
      );
      setSession(next);
    } catch (error) {
      toast.danger("Generation could not start", (error as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // The builder asked something and is waiting. Answering resumes the same
  // conversation over in the harness, so everything already established stays
  // in context -- this is not a new build.
  const sendAnswer = async () => {
    const text = answer.trim();
    if (!text || !activeSessionId) return;
    try {
      setBusy(true);
      const next = await core<PromptSession>(
        `/api/v1/prompt-builder/sessions/${encodeURIComponent(activeSessionId)}/messages`,
        {
          method: "POST",
          body: JSON.stringify({
            text,
            // Per message, never per session: one key for the whole session
            // would deduplicate a legitimate second answer.
            client_message_id: crypto.randomUUID(),
          }),
        },
      );
      setAnswer("");
      // The reply runs as a background turn, so `next` comes back `running`
      // and the poller and clock take over from here -- the same path a
      // fresh build follows.
      setSession(next);
    } catch (error) {
      toast.danger("Could not send your answer", (error as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const continueToStudio = () => {
    if (!activeSessionId) return;
    setDirty(false);
    navigate(
      `/home/create-agent?autobuilder_session_id=${encodeURIComponent(activeSessionId)}`,
    );
  };

  const hasUnpublishedWork = dirty || Boolean(activeSessionId && session?.status !== "done");
  const discardSession = useCallback(async () => {
    if (activeSessionId && session?.status !== "done") {
      await core(`/api/v1/prompt-builder/sessions/${encodeURIComponent(activeSessionId)}`, {
        method: "DELETE",
      });
    }
    setDirty(false);
  }, [activeSessionId, session?.status]);
  const requestDiscard = useDiscardWarning({ dirty: hasUnpublishedWork, discard: discardSession });
  const goBack = () => {
    void requestDiscard(() => navigate("/"));
  };

  const activeStageIndex = useMemo(
    () => Math.max(0, stages.findIndex(([key]) => key === session?.stage)),
    [session?.stage],
  );

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center bg-page">
        <Loader2 className="h-5 w-5 animate-spin text-brand" />
      </div>
    );
  }

  if (!session) {
    return (
      <div className="flex flex-1 items-center justify-center bg-page text-sm text-danger">
        AutoBuilder session is unavailable.
      </div>
    );
  }

  const running = session.status === "running";
  const awaitingInput = session.status === "awaiting_input";
  const reviewing = session.status === "awaiting_review";
  const failed = session.status === "failed";
  const questions = session.clarification_questions || [];
  // "failed" counts: after a failed build the operator wants to retry the
  // generation, not be sent back to re-review a brief they already reviewed.
  const briefReviewed =
    session.stage === "clarifications"
    || session.stage === "ready"
    || session.stage === "failed";
  const blocked = Boolean(
    session.quality?.blocking_count
    || session.generation?.validation_status === "failed_review_required",
  );

  const addVariable = () => updateForm("variables", [...form.variables, { key: "", value: "" }]);
  const addTransfer = () => updateForm(
    "transfer_numbers",
    [...form.transfer_numbers, { label: "", number: "" }],
  );

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden bg-page">
      <header className="flex h-16 shrink-0 items-center justify-between gap-3 border-b border-line bg-elevated px-5">
        <div className="flex min-w-0 items-center gap-3">
          <button type="button" onClick={goBack} aria-label="Back to agents" className="rounded-md p-2 text-ink-3 hover:bg-soft hover:text-ink">
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="truncate text-sm font-bold text-ink">AutoBuilder</h1>
              <span className={cn(
                "rounded px-2 py-0.5 text-[10px] font-bold uppercase",
                failed
                  ? "bg-danger-soft text-danger"
                  : reviewing
                    ? "bg-success-soft text-success"
                    : awaitingInput
                      ? "bg-warning-soft text-warning"
                      : "bg-soft text-ink-2",
              )}>
                {awaitingInput ? "needs your answer" : session.status.replace(/_/g, " ")}
              </span>
            </div>
            <p className="truncate text-xs text-ink-3">
              {form.agent_name} / {activeSessionId || "Not saved"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {running && progressDismissed ? (
            <button
              type="button"
              onClick={() => setProgressDismissed(false)}
              className="inline-flex items-center gap-2 rounded-md border border-line bg-elevated px-3 py-2 text-xs font-bold text-ink hover:bg-soft"
            >
              <Loader2 className="h-3.5 w-3.5 animate-spin text-brand" />
              View progress
            </button>
          ) : reviewing ? (
            <button
              type="button"
              onClick={continueToStudio}
              disabled={blocked}
              className="inline-flex items-center gap-2 rounded-md bg-brand px-3 py-2 text-xs font-bold text-white hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-50"
            >
              <ChevronRight className="h-3.5 w-3.5" />
              Continue to Single Agent Studio
            </button>
          ) : !running && !awaitingInput && (
            <button
              type="button"
              onClick={briefReviewed ? start : evaluate}
              disabled={busy || gaps.length > 0}
              className="inline-flex items-center gap-2 rounded-md bg-brand px-3 py-2 text-xs font-bold text-white hover:bg-brand-hover disabled:cursor-not-allowed disabled:bg-line disabled:text-ink-3"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : failed ? <RefreshCw className="h-3.5 w-3.5" /> : <Sparkles className="h-3.5 w-3.5" />}
              {briefReviewed ? (failed ? "Retry generation" : "Generate prompt") : "Review brief"}
            </button>
          )}
        </div>
      </header>

      <div className="grid min-h-0 w-full flex-1 grid-cols-[240px_minmax(0,1fr)_300px] overflow-hidden">
        <aside className="min-h-0 overflow-y-auto border-r border-line bg-elevated p-5">
          <p className="font-mono text-[10px] font-bold uppercase text-ink-3">Builder sections</p>
          <div className="mt-4 space-y-1">
            {sections.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                type="button"
                disabled={running || reviewing}
                onClick={() => setActiveSection(id)}
                className={cn(
                  "flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-xs font-semibold",
                  activeSection === id && !briefReviewed ? "bg-brand-soft text-brand" : "text-ink-3 hover:bg-soft hover:text-ink",
                )}
              >
                <Icon className="h-3.5 w-3.5" />
                {label}
              </button>
            ))}
            {briefReviewed && !running && !reviewing && (
              <button type="button" className="flex w-full items-center gap-2 rounded-md bg-brand-soft px-3 py-2 text-left text-xs font-semibold text-brand">
                <ShieldCheck className="h-3.5 w-3.5" />
                Clarifications
              </button>
            )}
            {reviewing && (
              <>
                <div className="flex items-center gap-2 rounded-md bg-brand-soft px-3 py-2 text-xs font-semibold text-brand"><Sparkles className="h-3.5 w-3.5" />Generated prompt</div>
                <div className="flex items-center gap-2 rounded-md px-3 py-2 text-xs font-semibold text-ink-3"><Wrench className="h-3.5 w-3.5" />Tools and variables</div>
                <div className="flex items-center gap-2 rounded-md px-3 py-2 text-xs font-semibold text-ink-3"><ShieldCheck className="h-3.5 w-3.5" />Quality review</div>
              </>
            )}
          </div>
        </aside>

        <main className="min-h-0 min-w-0 overflow-y-auto bg-[#f7f7f5] p-6">
          {awaitingInput ? (
            // The builder needs something the brief did not settle. It asks one
            // question at a time; answering resumes the same conversation
            // rather than starting a new build.
            <div className="mx-auto max-w-2xl space-y-5">
              <div className="flex items-center gap-2 text-xs font-bold uppercase text-ink-3">
                <MessageCircle className="h-3.5 w-3.5" />
                One question before it can finish
              </div>
              <div className="border border-line bg-white p-5">
                <p className="whitespace-pre-wrap text-sm leading-6 text-ink">
                  {session.agent_question || "The builder needs more detail to continue."}
                </p>
              </div>
              <div>
                <textarea
                  autoFocus
                  className={cn(fieldClass(), "min-h-[120px] resize-y")}
                  placeholder="Answer in your own words. Say so if you are not sure - it will pick a sensible default and note the assumption."
                  value={answer}
                  onChange={(event) => setAnswer(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
                      event.preventDefault();
                      void sendAnswer();
                    }
                  }}
                />
                <div className="mt-3 flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => void sendAnswer()}
                    disabled={busy || !answer.trim()}
                    className="inline-flex items-center gap-2 bg-ink px-4 py-2 text-xs font-bold text-white disabled:opacity-50"
                  >
                    {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                    Send answer
                  </button>
                  <span className="text-[11px] text-ink-3">
                    It will keep building once you answer. This takes a few minutes.
                  </span>
                </div>
              </div>
            </div>
          ) : reviewing ? (
            <div className="space-y-6">
              <div>
                <h2 className="text-sm font-semibold text-ink">Review generated agent</h2>
                
                {/* The builder's own reasoning for the architecture it picked.
                    It was being returned and thrown away; it is the one part
                    of the result that explains the rest. */}
                {session.generation?.rationale ? (
                  <p className="mt-2 whitespace-pre-line border-l-2 border-line pl-3 text-sm text-ink-3">
                    {session.generation.rationale}
                  </p>
                ) : null}
              </div>
              {blocked && (
                <div className="flex gap-3 border border-danger/30 bg-danger-soft p-4 text-sm text-danger">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  <div>
                    <p className="font-bold">Continue is blocked</p>
                    <p className="mt-1 text-xs">Critical compiler issues must be resolved by regenerating the brief.</p>
                  </div>
                </div>
              )}
              <section>
                <h3 className="mb-2 text-xs font-bold uppercase text-ink-2">First message</h3>
                <div className="rounded-md border border-line bg-elevated p-4 text-sm leading-6 text-ink">
                  {session.generation?.first_message || "No first message generated."}
                </div>
              </section>
              <section>
                <h3 className="mb-2 text-xs font-bold uppercase text-ink-2">System prompt</h3>
                <pre className="max-h-[620px] overflow-auto whitespace-pre-wrap rounded-md border border-line bg-elevated p-4 font-mono text-xs leading-6 text-ink">
                  {session.generation?.system_prompt}
                </pre>
              </section>
            </div>
          ) : briefReviewed && !running ? (
            <div className="mx-auto max-w-3xl">
              <div className="mb-6">
                <h2 className="text-sm font-semibold text-ink">Reviewer clarifications</h2>
                
              </div>
              {questions.length ? (
                <div className="space-y-4">
                  {questions.map((question, index) => (
                    <label key={question} className="block rounded-md border border-line bg-elevated p-4">
                      <span className="text-sm font-bold text-ink">{index + 1}. {question}</span>
                      <textarea
                        rows={3}
                        value={clarifications[question] || ""}
                        onChange={(event) => {
                          setClarifications((current) => ({ ...current, [question]: event.target.value }));
                          setDirty(true);
                        }}
                        placeholder="Optional. Leave blank when the compiler should use its safest fallback."
                        className={cn(fieldClass(), "mt-3 resize-y")}
                      />
                    </label>
                  ))}
                </div>
              ) : (
                <div className="rounded-md border border-success/30 bg-success-soft p-4 text-sm text-success">
                  The brief has no material gaps. It is ready to compile.
                </div>
              )}
            </div>
          ) : (
            <div className="mx-auto max-w-4xl">
              {activeSection === "persona" && (
                <div>
                  <h2 className="text-sm font-semibold text-ink">Agent persona</h2>
                  
                  <div className="mt-6 grid gap-5 md:grid-cols-2">
                    <label className="block">
                      <span className="mb-1.5 block text-xs font-bold text-ink">Company name *</span>
                      <input value={form.company_name} onChange={(event) => updateForm("company_name", event.target.value)} className={fieldClass(!form.company_name.trim())} />
                    </label>
                    <label className="block">
                      <span className="mb-1.5 block text-xs font-bold text-ink">Agent name</span>
                      <input value={form.agent_name} onChange={(event) => updateForm("agent_name", event.target.value)} className={fieldClass()} />
                    </label>
                    <label className="block">
                      <span className="mb-1.5 block text-xs font-bold text-ink">Industry</span>
                      <select value={form.industry} onChange={(event) => updateForm("industry", event.target.value)} className={fieldClass()}>
                        <option value="">Select industry</option>
                        {INDUSTRIES.map((industry) => <option key={industry} value={industry}>{industry}</option>)}
                      </select>
                    </label>
                    <label className="block">
                      <span className="mb-1.5 block text-xs font-bold text-ink">Deployment region</span>
                      <select value={form.region} onChange={(event) => updateForm("region", event.target.value)} className={fieldClass()}>
                        {REGIONS.map(([code, label]) => <option key={code || "none"} value={code}>{label}</option>)}
                      </select>
                    </label>
                    <div>
                      <span className="mb-1.5 block text-xs font-bold text-ink">Call direction</span>
                      <div className="inline-flex rounded-md border border-line bg-elevated p-1">
                        {(["inbound", "outbound"] as const).map((direction) => (
                          <button key={direction} type="button" onClick={() => updateForm("call_direction", direction)} className={cn("rounded px-4 py-1.5 text-xs font-semibold capitalize", form.call_direction === direction ? "bg-ink text-canvas" : "text-ink-2 hover:bg-soft")}>{direction}</button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <span className="mb-1.5 block text-xs font-bold text-ink">Voice gender</span>
                      <div className="inline-flex rounded-md border border-line bg-elevated p-1">
                        {(["Female", "Male"] as const).map((gender) => (
                          <button key={gender} type="button" onClick={() => updateForm("voice_gender", gender)} className={cn("rounded px-4 py-1.5 text-xs font-semibold", form.voice_gender === gender ? "bg-ink text-canvas" : "text-ink-2 hover:bg-soft")}>{gender}</button>
                        ))}
                      </div>
                    </div>
                    <label className="block">
                      <span className="mb-1.5 block text-xs font-bold text-ink">Primary language</span>
                      <select value={form.primary_language} onChange={(event) => updateForm("primary_language", event.target.value)} className={fieldClass()}>
                        {LANGUAGES.map((language) => <option key={language} value={language}>{language}</option>)}
                      </select>
                    </label>
                    <label className="block">
                      <span className="mb-1.5 block text-xs font-bold text-ink">Secondary language</span>
                      <select value={form.secondary_language} onChange={(event) => updateForm("secondary_language", event.target.value)} className={fieldClass()}>
                        <option value="None">None</option>
                        {LANGUAGES.filter((language) => language !== form.primary_language).map((language) => <option key={language} value={language}>{language}</option>)}
                      </select>
                    </label>
                    <label className="block md:col-span-2">
                      <span className="mb-1.5 block text-xs font-bold text-ink">Voice tone</span>
                      <select value={form.voice_tone} onChange={(event) => updateForm("voice_tone", event.target.value)} className={fieldClass()}>
                        {["professional", "friendly", "empathetic", "authoritative", "casual", "persuasive"].map((tone) => <option key={tone} value={tone}>{tone}</option>)}
                      </select>
                    </label>
                  </div>
                </div>
              )}

              {activeSection === "settings" && (
                <div>
                  <h2 className="text-sm font-semibold text-ink">Call handling settings</h2>
                  
                  <div className="mt-6 border-y border-line">
                    <Toggle label="Disclose AI identity when relevant" checked={form.disclose_ai} onChange={(value) => updateForm("disclose_ai", value)} />
                    <Toggle label="Recording consent required" checked={form.recording_consent} onChange={(value) => updateForm("recording_consent", value)} />
                    <Toggle label="Enable live transfer" checked={form.live_transfer_enabled} onChange={(value) => updateForm("live_transfer_enabled", value)} />
                  </div>
                  <div className="mt-5 grid gap-5 md:grid-cols-2">
                    {form.recording_consent && (
                      <label className="block md:col-span-2">
                        <span className="mb-1.5 block text-xs font-bold text-ink">Exact disclosure line *</span>
                        <input value={form.disclosure_text} onChange={(event) => updateForm("disclosure_text", event.target.value)} className={fieldClass(!form.disclosure_text.trim())} />
                      </label>
                    )}
                    <label className="block">
                      <span className="mb-1.5 block text-xs font-bold text-ink">Off-topic handling</span>
                      <select value={form.digression_handling} onChange={(event) => updateForm("digression_handling", event.target.value)} className={fieldClass()}>
                        <option>Answer briefly, then resume the script</option>
                        <option>Politely refuse and resume the script</option>
                        <option>Transfer to a human agent</option>
                      </select>
                    </label>
                    <label className="block">
                      <span className="mb-1.5 block text-xs font-bold text-ink">Fallback after misunderstanding</span>
                      <select value={form.retry_fallback} onChange={(event) => updateForm("retry_fallback", event.target.value)} className={fieldClass()}>
                        <option>Transfer to a human agent</option>
                        <option>Offer a callback</option>
                        <option>End the call politely</option>
                      </select>
                    </label>
                    <label className="block">
                      <span className="mb-1.5 block text-xs font-bold text-ink">Maximum retries</span>
                      <input type="number" min={1} max={5} value={form.max_retries} onChange={(event) => updateForm("max_retries", Math.min(5, Math.max(1, Number(event.target.value) || 1)))} className={fieldClass()} />
                    </label>
                  </div>
                  {form.live_transfer_enabled && (
                    <div className="mt-6 border-t border-line pt-5">
                      <div className="flex items-center justify-between">
                        <h3 className="text-sm font-bold text-ink">Transfer destinations</h3>
                        <button type="button" onClick={addTransfer} title="Add transfer destination" className="rounded-md p-2 text-ink-2 hover:bg-soft hover:text-ink"><Plus className="h-4 w-4" /></button>
                      </div>
                      <div className="mt-3 space-y-2">
                        {form.transfer_numbers.map((target, index) => (
                          <div key={index} className="grid grid-cols-[1fr_1fr_auto] gap-2">
                            <input placeholder="Label" value={target.label} onChange={(event) => updateForm("transfer_numbers", form.transfer_numbers.map((item, itemIndex) => itemIndex === index ? { ...item, label: event.target.value } : item))} className={fieldClass()} />
                            <input placeholder="+91..." value={target.number} onChange={(event) => updateForm("transfer_numbers", form.transfer_numbers.map((item, itemIndex) => itemIndex === index ? { ...item, number: event.target.value } : item))} className={fieldClass(!target.number.trim())} />
                            <button type="button" title="Remove destination" onClick={() => updateForm("transfer_numbers", form.transfer_numbers.filter((_, itemIndex) => itemIndex !== index))} className="rounded-md p-2 text-ink-3 hover:bg-danger-soft hover:text-danger"><Trash2 className="h-4 w-4" /></button>
                          </div>
                        ))}
                      </div>
                      <h3 className="mt-5 text-xs font-bold uppercase text-ink-2">Transfer triggers</h3>
                      <div className="mt-2 grid gap-2 md:grid-cols-2">
                        {TRANSFER_TRIGGERS.map((trigger) => (
                          <label key={trigger} className="flex items-center gap-2 rounded-md border border-line bg-elevated p-3 text-xs font-semibold text-ink">
                            <input type="checkbox" checked={form.transfer_triggers.includes(trigger)} onChange={() => updateForm("transfer_triggers", form.transfer_triggers.includes(trigger) ? form.transfer_triggers.filter((item) => item !== trigger) : [...form.transfer_triggers, trigger])} />
                            {trigger}
                          </label>
                        ))}
                      </div>
                      <label className="mt-5 block">
                        <span className="mb-1.5 block text-xs font-bold text-ink">If nobody can take the transfer</span>
                        <textarea rows={3} value={form.after_hours_behavior} onChange={(event) => updateForm("after_hours_behavior", event.target.value)} className={fieldClass()} />
                      </label>
                    </div>
                  )}
                </div>
              )}

              {activeSection === "objective" && (
                <div>
                  <h2 className="text-sm font-semibold text-ink">Call purpose and preloaded data</h2>
                  
                  <label className="mt-6 block">
                    <span className="mb-1.5 block text-xs font-bold text-ink">Call purpose *</span>
                    <textarea rows={7} value={form.call_purpose} onChange={(event) => updateForm("call_purpose", event.target.value)} className={cn(fieldClass(!form.call_purpose.trim()), "resize-y leading-6")} />
                  </label>
                  <div className="mt-6 flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-bold text-ink">Pre-call variables</h3>
                      <p className="mt-1 text-xs text-ink-3">Values supplied by CRM or API before the call begins.</p>
                    </div>
                    <button type="button" onClick={addVariable} title="Add variable" className="rounded-md p-2 text-ink-2 hover:bg-soft hover:text-ink"><Plus className="h-4 w-4" /></button>
                  </div>
                  <div className="mt-3 space-y-2">
                    {form.variables.map((variable, index) => (
                      <div key={index} className="grid grid-cols-[1fr_1fr_auto] gap-2">
                        <input placeholder="customer_name" value={variable.key} onChange={(event) => updateForm("variables", form.variables.map((item, itemIndex) => itemIndex === index ? { ...item, key: event.target.value } : item))} className={fieldClass()} />
                        <input placeholder="Example value" value={variable.value} onChange={(event) => updateForm("variables", form.variables.map((item, itemIndex) => itemIndex === index ? { ...item, value: event.target.value } : item))} className={fieldClass()} />
                        <button type="button" title="Remove variable" onClick={() => updateForm("variables", form.variables.filter((_, itemIndex) => itemIndex !== index))} className="rounded-md p-2 text-ink-3 hover:bg-danger-soft hover:text-danger"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    ))}
                    {!form.variables.length && <p className="rounded-md border border-dashed border-line p-4 text-xs text-ink-3">No pre-call variables configured.</p>}
                  </div>
                </div>
              )}

              {activeSection === "conversation" && (
                <div>
                  <h2 className="text-sm font-semibold text-ink">Conversation flow</h2>
                  
                  <label className="mt-6 block">
                    <span className="mb-1.5 block text-xs font-bold text-ink">Opening message</span>
                    <textarea rows={3} value={form.opening_message} onChange={(event) => updateForm("opening_message", event.target.value)} placeholder="Leave blank to generate a grounded opening." className={cn(fieldClass(), "resize-y")} />
                  </label>
                  <label className="mt-5 block">
                    <span className="mb-1.5 block text-xs font-bold text-ink">Call flow *</span>
                    <textarea rows={14} value={form.call_flow} onChange={(event) => updateForm("call_flow", event.target.value)} placeholder={"1. Introduce the company and reason for the call\n2. Confirm the caller's need\n3. Collect the required details\n4. Confirm the outcome\n5. Close the call"} className={cn(fieldClass(!form.call_flow.trim()), "resize-y font-mono text-xs leading-6")} />
                  </label>
                </div>
              )}

              {activeSection === "knowledge" && (
                <div>
                  <h2 className="text-sm font-semibold text-ink">Knowledge base and guardrails</h2>
                  
                  <div className="mt-5 border-y border-line">
                    <Toggle label="Enable knowledge extraction" checked={form.kb_enabled} onChange={(value) => updateForm("kb_enabled", value)} />
                  </div>
                  {form.kb_enabled ? (
                    <label className="mt-5 block">
                      <span className="mb-1.5 block text-xs font-bold text-ink">Knowledge content *</span>
                      <textarea rows={18} value={form.kb_content} onChange={(event) => updateForm("kb_content", event.target.value)} placeholder="FAQs, prices, policies, hours, locations, troubleshooting steps and approved objection responses." className={cn(fieldClass(!form.kb_content.trim()), "resize-y leading-6")} />
                      <span className="mt-1 block text-right font-mono text-[10px] text-ink-3">{form.kb_content.length.toLocaleString()} / 24,000</span>
                    </label>
                  ) : (
                    <div className="mt-5 rounded-md border border-line bg-elevated p-4 text-sm text-ink-2">
                      The compiler will use only the persona, call purpose, flow and clarification answers.
                    </div>
                  )}
                </div>
              )}

              {failed && (
                <div className="mt-6 flex gap-3 border border-danger/30 bg-danger-soft p-4 text-sm text-danger">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  <div>
                    <p className="font-bold">Generation failed</p>
                    <p className="mt-1 text-xs leading-5">{session.error || "The generation session did not finish."}</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </main>

        <aside className="min-h-0 overflow-y-auto border-l border-line bg-elevated p-5">
          <p className="font-mono text-[10px] font-bold uppercase text-ink-3">Readiness</p>
          {reviewing ? (
            <>
              <div className="mt-4 flex items-baseline gap-2">
                <span className="text-3xl font-bold capitalize text-ink">
                  {session.generation?.architecture ?? "-"}
                </span>
                <span className="text-xs font-semibold text-ink-3">architecture</span>
              </div>
              <div className="mt-1 text-[10px] font-bold uppercase text-ink-3">
                ${(session.quality?.cost ?? 0).toFixed(2)} / {(session.quality?.tokens ?? 0).toLocaleString()} tokens
              </div>
              <div className="mt-5 space-y-5">
                <div>
                  <p className="text-xs font-bold text-ink">Tools</p>
                  <div className="mt-2 space-y-2">
                    {(session.generation?.tools ?? []).length ? (
                      (session.generation?.tools ?? []).map((tool, index) => (
                        <div key={`${tool.name}-${index}`} className="rounded-md bg-soft px-3 py-2">
                          <p className="font-mono text-xs font-bold text-ink">{tool.name}</p>
                          {tool.why && (
                            <p className="mt-0.5 text-[11px] leading-4 text-ink-2">{tool.why}</p>
                          )}
                          {tool.used_in && (
                            <p className="mt-1 text-[10px] font-semibold uppercase text-ink-3">
                              {tool.used_in}
                            </p>
                          )}
                        </div>
                      ))
                    ) : (
                      <p className="text-[11px] leading-4 text-ink-2">
                        No tools attached. The agent decided this call needs none.
                      </p>
                    )}
                  </div>
                </div>
                <div>
                  <p className="text-xs font-bold text-ink">Variables</p>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {(session.generation?.variables ?? []).map((variable, index) => (
                      <span key={`${variable.key}-${index}`} className="rounded bg-soft px-2 py-1 font-mono text-[10px] text-ink-2">{variable.key}</span>
                    ))}
                  </div>
                </div>
                {(session.generation?.proposed_tools ?? []).length > 0 && (
                  <div>
                    <p className="text-xs font-bold text-ink">Not possible today</p>
                    <p className="mt-1 text-[11px] leading-4 text-ink-3">
                      The agent designed the call to work without these.
                    </p>
                    <div className="mt-2 space-y-2">
                      {(session.generation?.proposed_tools ?? []).map((tool, index) => (
                        <div key={`${tool.name}-${index}`} className="border-l-2 border-warning pl-3">
                          <p className="font-mono text-[11px] font-bold text-ink">{tool.name}</p>
                          <p className="mt-0.5 text-[11px] leading-4 text-ink-2">{tool.purpose}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div>
                  <p className="text-xs font-bold text-ink">Self-check</p>
                  <div className="mt-2 space-y-3">
                    {(session.quality?.findings ?? []).filter((f) => !f.passed).length ? (
                      (session.quality?.findings ?? [])
                        .filter((finding) => !finding.passed)
                        .map((finding, index) => (
                          <div key={`${finding.rule}-${index}`} className={cn("border-l-2 pl-3", finding.severity === "critical" ? "border-danger" : finding.severity === "major" ? "border-warning" : "border-line")}>
                            <p className="text-[10px] font-bold uppercase text-ink-3">{finding.severity}</p>
                            <p className="mt-0.5 text-xs font-semibold text-ink">{finding.rule}</p>
                            <p className="mt-0.5 text-[11px] leading-4 text-ink-2">{finding.evidence}</p>
                          </div>
                        ))
                    ) : (
                      <div className="flex items-center gap-2 text-xs font-semibold text-success"><Check className="h-3.5 w-3.5" />Passed its own checks</div>
                    )}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="mt-4 space-y-4">
              <div>
                <p className="text-xs font-bold text-ink">Required fields</p>
                {gaps.length ? (
                  <div className="mt-2 space-y-2">
                    {gaps.map((gap) => <div key={gap} className="flex items-center gap-2 text-xs text-warning"><Circle className="h-3 w-3" />{gap}</div>)}
                  </div>
                ) : (
                  <div className="mt-2 flex items-center gap-2 text-xs font-semibold text-success"><Check className="h-3.5 w-3.5" />Brief is complete</div>
                )}
              </div>
              <div className="border-t border-line pt-4 text-xs leading-5 text-ink-2">
                <p><Languages className="mr-1 inline h-3.5 w-3.5" />{form.primary_language}{form.secondary_language !== "None" ? ` + ${form.secondary_language}` : ""}</p>
                <p className="mt-1"><Bot className="mr-1 inline h-3.5 w-3.5" />{form.call_direction} / {form.voice_tone}</p>
                <p className="mt-1"><Wrench className="mr-1 inline h-3.5 w-3.5" />{form.live_transfer_enabled ? `${form.transfer_numbers.filter((item) => item.number.trim()).length} transfer target(s)` : "No live transfer"}</p>
              </div>
            </div>
          )}
        </aside>
      </div>

      {running && !progressDismissed && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/35 p-4 backdrop-blur-[1px]"
          role="dialog"
          aria-modal="true"
          aria-labelledby="generation-progress-title"
        >
          <div className="flex max-h-[min(720px,calc(100vh-32px))] w-full max-w-xl flex-col overflow-hidden rounded-md border border-line bg-elevated shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-line px-6 py-5">
              <div className="flex min-w-0 items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-brand-soft text-brand">
                  <Sparkles className="h-5 w-5" />
                </span>
                <div className="min-w-0">
                  <h2 id="generation-progress-title" className="text-base font-bold text-ink">
                    Generating your agent
                  </h2>
                  <p className="mt-1 truncate text-xs text-ink-3">
                    {form.agent_name} / {activeSessionId}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setProgressDismissed(true)}
                title="Run in background"
                aria-label="Run generation in background"
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-ink-3 hover:bg-soft hover:text-ink"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="border-b border-line bg-soft px-6 py-5">
              <div className="flex items-end justify-between gap-4">
                <div>
                  <p className="font-mono text-[10px] font-bold uppercase text-brand">
                    Building
                  </p>
                  <p className="mt-1 text-sm font-bold text-ink">
                    {stages[activeStageIndex]?.[1] || "Reading the brief"}
                  </p>
                </div>
                <span className="font-mono text-xs font-bold text-ink-2">
                  {Math.floor(elapsed / 60)}:{String(elapsed % 60).padStart(2, "0")}
                </span>
              </div>
              {/* The agent decides its own steps, so there is no completed
                  fraction to report. A bar that fills on a timer would be
                  inventing progress; this one just shows it is working. */}
              <div className="mt-4 h-2 overflow-hidden rounded-full bg-line">
                <div
                  className="h-full w-1/3 animate-[shimmer_1.6s_ease-in-out_infinite] rounded-full bg-brand"
                  style={{
                    animation: "autobuilder-slide 1.6s ease-in-out infinite",
                  }}
                />
              </div>
              <style>{`@keyframes autobuilder-slide {
                0% { transform: translateX(-100%); }
                100% { transform: translateX(300%); }
              }`}</style>
              <p className="mt-2 text-[11px] text-ink-3">
                A build usually takes {Math.round(TYPICAL_BUILD_SECONDS / 60)}-7 minutes.
                You can close this and come back; it keeps running.
              </p>
            </div>

            <div className="min-h-0 overflow-y-auto px-6 py-4">
              <div className="space-y-2 text-xs leading-5 text-ink-2">
                <p className="font-semibold text-ink">What it is doing</p>
                <p>
                  Reading your brief and the tools this agent can actually call,
                  choosing an architecture, then writing the prompt and checking
                  it against the publishing rules before returning it.
                </p>
                <p>
                  If something in the brief is genuinely ambiguous it will stop
                  and ask you rather than guess.
                </p>
              </div>
            </div>

            <div className="flex items-center justify-between gap-4 border-t border-line bg-soft px-6 py-4">
              <p className="text-[11px] leading-5 text-ink-3">
                Generation continues if this window is minimized.
              </p>
              <button
                type="button"
                onClick={() => setProgressDismissed(true)}
                className="inline-flex shrink-0 items-center gap-2 rounded-md border border-line bg-elevated px-3 py-2 text-xs font-bold text-ink hover:bg-page"
              >
                <Minimize2 className="h-3.5 w-3.5" />
                Run in background
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
